import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faShareNodes, faTrash, faUsers } from "@fortawesome/free-solid-svg-icons";
import {
  getTeacherCourse,
  getTeacherCourseCohorts,
  getCoursePlanItems,
  postTeacherCourseArchive,
  type CoursePlanItem,
  type TeacherCourseCohortRecord,
  type TeacherCourseRecord,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { LessonPreviewTile } from "./LessonPreviewTile";
import { QuizPreviewTile } from "./QuizPreviewTile";
import { TeacherCourseDetailsSkeleton } from "./TeacherCourseDetailsSkeleton";
import { textColorsForBackground } from "./lessonCardColors";
import { DEFAULT_COURSE_SCRIM_COLOR } from "./courseScrim";
import { buildDeleteCourseConfirmMessage } from "./lessonDeleteMessages";

function publishPill(status?: string) {
  if (status === "approved") return <span className="teacher-pill teacher-pill--published">Published</span>;
  if (status === "pending_review") return <span className="teacher-pill teacher-pill--pending">In review</span>;
  return null;
}

const DEFAULT_DETAILS_BG = "linear-gradient(160deg, #1b4d2e 0%, #0f2a1a 55%, #0a120d 100%)";

export default function TeacherCourseDetailsPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const navigate = useNavigate();
  const [course, setCourse] = useState<TeacherCourseRecord | null>(null);
  const [inspirationCount, setInspirationCount] = useState(0);
  const [planItems, setPlanItems] = useState<CoursePlanItem[]>([]);
  const [cohorts, setCohorts] = useState<TeacherCourseCohortRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const detailsPath = courseId ? `/teacher/courses/${courseId}` : "/teacher/courses";
  const managePath = courseId ? `/teacher/courses/${courseId}/manage` : "/teacher/courses";

  const refresh = useCallback(async () => {
    if (!bearer || !courseId) return;
    setError(null);
    setLoading(true);
    try {
      const [courseRes, plan, ch] = await Promise.all([
        getTeacherCourse(bearer, courseId),
        getCoursePlanItems(bearer, courseId),
        getTeacherCourseCohorts(bearer, courseId),
      ]);
      setCourse(courseRes.course);
      setInspirationCount(courseRes.inspirationCount ?? 0);
      setPlanItems(plan.items);
      setCohorts(ch.cohorts);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load course");
      setCourse(null);
    } finally {
      setLoading(false);
    }
  }, [bearer, courseId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleDeleteCourse() {
    if (!bearer || !courseId || !course || deleting || course.status === "archived") return;
    const activeCohorts = course.activeCohorts ?? 0;
    if (!window.confirm(buildDeleteCourseConfirmMessage(course.title, activeCohorts))) return;
    setDeleting(true);
    setError(null);
    try {
      await postTeacherCourseArchive(bearer, courseId);
      navigate("/teacher/courses");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete course");
      setDeleting(false);
    }
  }

  const accent = course?.accentColor?.trim() || null;
  const pageBackground = accent || DEFAULT_DETAILS_BG;
  const scrimColor = accent || DEFAULT_COURSE_SCRIM_COLOR;
  const pageColors = useMemo(() => textColorsForBackground(pageBackground), [pageBackground]);
  const lessonCount = planItems.filter((i) => i.itemKind === "lesson").length;
  const quizCount = planItems.filter((i) => i.itemKind === "quiz").length;
  const published = course?.communityPublishStatus === "approved";
  const startCohortHref = courseId
    ? `/teacher/cohorts?courseId=${encodeURIComponent(courseId)}&create=1`
    : "/teacher/cohorts";

  if (!courseId) {
    return (
      <div className="teacher-page">
        <p className="warn-banner">Missing course id.</p>
      </div>
    );
  }

  if (loading && !course) {
    return <TeacherCourseDetailsSkeleton />;
  }

  return (
    <div
      className="teacher-course-details"
      data-tone={pageColors.tone}
      style={
        {
          "--course-details-bg": pageBackground,
          "--course-details-scrim": scrimColor,
          "--course-details-fg": pageColors.title,
          "--course-details-muted": pageColors.blurb,
        } as CSSProperties
      }
    >
      <div
        className={`teacher-course-details-hero${course?.heroImageUrl ? " teacher-course-details-hero--image" : ""}`}
      >
        {course?.heroImageUrl ? (
          <img
            className="teacher-course-details-hero-img"
            src={course.heroImageUrl}
            alt=""
            crossOrigin="anonymous"
          />
        ) : null}
        <div className="teacher-course-details-hero-scrim" aria-hidden />
        <div className="teacher-course-details-hero-inner">
          <nav className="teacher-course-details-breadcrumb">
            <Link to="/teacher/courses">Courses</Link>
            <span aria-hidden> / </span>
            <span>{course?.title ?? "…"}</span>
          </nav>

          {course ? (
            <>
              <div className="teacher-course-details-badges">
                {publishPill(course.communityPublishStatus)}
                {course.sourceCommunityCourseId ? (
                  <span className="teacher-pill teacher-pill--inspired">Inspired</span>
                ) : null}
              </div>
              <h1 className="teacher-course-details-title">{course.title}</h1>
              <p className="teacher-course-details-lede">
                {course.description?.trim() || "No description yet."}
              </p>
              <dl className="teacher-course-details-stats">
                <div>
                  <dt>Lessons</dt>
                  <dd>{lessonCount}</dd>
                </div>
                {quizCount > 0 ? (
                  <div>
                    <dt>Quizzes</dt>
                    <dd>{quizCount}</dd>
                  </div>
                ) : null}
                <div>
                  <dt>Active cohorts</dt>
                  <dd>{course.activeCohorts ?? 0}</dd>
                </div>
                <div>
                  <dt>Version</dt>
                  <dd>v{course.version ?? 1}</dd>
                </div>
                {published ? (
                  <div>
                    <dt>Community copies</dt>
                    <dd>{inspirationCount}</dd>
                  </div>
                ) : null}
              </dl>
              <div className="teacher-course-details-actions">
                <Link to={managePath} className="btn primary" style={{ textDecoration: "none" }}>
                  <FontAwesomeIcon icon={faPen} /> Manage
                </Link>
                {course.status !== "archived" ? (
                  <Link to={startCohortHref} className="btn secondary" style={{ textDecoration: "none" }}>
                    <FontAwesomeIcon icon={faUsers} /> Start cohort
                  </Link>
                ) : null}
                {course.status !== "archived" ? (
                  <button
                    type="button"
                    className="btn secondary danger"
                    disabled={deleting}
                    onClick={() => void handleDeleteCourse()}
                  >
                    <FontAwesomeIcon icon={faTrash} /> {deleting ? "Deleting…" : "Delete"}
                  </button>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      </div>

      {error ? (
        <div className="warn-banner teacher-course-details-pad" role="alert">
          {error}
        </div>
      ) : null}

      <section className="teacher-course-details-section teacher-course-details-pad">
        <h2 className="teacher-course-details-section-title">Course plan</h2>
        {planItems.length === 0 ? (
          <p className="teacher-course-details-muted">
            No items yet.{" "}
            <Link to={managePath} className="teacher-course-details-link">
              Add lessons or quizzes in Manage
            </Link>
            .
          </p>
        ) : (
          <ol className="teacher-course-details-lessons">
            {planItems.map((item, index) => (
              <li key={item.planItemId} className="teacher-course-details-lesson">
                <span className="teacher-course-details-lesson-num" aria-hidden>
                  {index + 1}
                </span>
                {item.itemKind === "lesson" ? (
                  <LessonPreviewTile
                    lessonId={item.lesson.id}
                    lessonTitle={item.lesson.title}
                    courseId={courseId}
                    iconName={item.lesson.iconName}
                    iconBgColor={item.lesson.iconBgColor}
                    iconFgColor={item.lesson.iconFgColor}
                    workspaceMode={item.lesson.workspaceMode}
                    returnTo={detailsPath}
                    size="md"
                  />
                ) : (
                  <QuizPreviewTile
                    quizId={item.quiz.id}
                    quizTitle={item.quiz.title}
                    courseId={courseId}
                    iconName={item.quiz.iconName}
                    iconBgColor={item.quiz.iconBgColor}
                    iconFgColor={item.quiz.iconFgColor}
                    size="md"
                  />
                )}
                <div className="teacher-course-details-lesson-copy">
                  <h3>{item.itemKind === "lesson" ? item.lesson.title : item.quiz.title}</h3>
                  <p>
                    {item.itemKind === "lesson"
                      ? item.lesson.description?.trim() || "Preview this lesson as a student would see it."
                      : item.quiz.description?.trim() || "Review the full question pool with correct answers."}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="teacher-course-details-section teacher-course-details-pad">
        <h2 className="teacher-course-details-section-title">Cohorts using this course</h2>
        {cohorts.length === 0 ? (
          <p className="teacher-course-details-muted">
            No cohorts yet.{" "}
            {course?.status !== "archived" ? (
              <Link to={startCohortHref} className="teacher-course-details-link">
                Start one
              </Link>
            ) : (
              "This course is archived."
            )}
          </p>
        ) : (
          <div className="teacher-course-details-cohort-list">
            {cohorts.map((c) => (
              <Link key={c.id} to={`/teacher/cohorts/${c.id}`} className="teacher-course-details-cohort-card">
                <strong>{c.label}</strong>
                <span className={`badge ${c.status === "archived" ? "badge-muted" : ""}`}>{c.status}</span>
                <span className="teacher-course-details-muted small">
                  Updated {new Date(c.updatedAt).toLocaleDateString()}
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>

      {published ? (
        <section className="teacher-course-details-section teacher-course-details-pad">
          <h2 className="teacher-course-details-section-title">
            <FontAwesomeIcon icon={faShareNodes} /> Community
          </h2>
          <p className="teacher-course-details-muted">
            This course is published to the community
            {inspirationCount > 0
              ? ` and has been copied ${inspirationCount} time${inspirationCount === 1 ? "" : "s"} by other teachers.`
              : ". No copies yet."}
          </p>
        </section>
      ) : null}
    </div>
  );
}
